import { useState } from 'react';
import { 
  CreditCard, DollarSign, CheckCircle, Receipt, Printer, 
  Send, User, Mail, Phone, ChevronDown, Plus, Minus, Trash2
} from 'lucide-react';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

interface Invoice {
  id: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  date: string;
  items: LineItem[];
  subtotal: number;
  tax: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  paymentMethod?: 'cash' | 'card' | 'transfer';
}

export default function MobilePOS() {
  const [invoice, setInvoice] = useState<Invoice>({
    id: 'INV-' + Date.now().toString().slice(-6),
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    date: new Date().toISOString().split('T')[0],
    items: [
      { id: '1', description: 'Servicio de plomería', quantity: 1, unitPrice: 1500 },
    ],
    subtotal: 1500,
    tax: 240,
    total: 1740,
    status: 'draft',
  });

  const [showPreview, setShowPreview] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'transfer'>('card');

  const updateItem = (id: string, field: keyof LineItem, value: string | number) => {
    setInvoice(prev => {
      const newItems = prev.items.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      );
      const subtotal = newItems.reduce((acc, item) => acc + (item.quantity * item.unitPrice), 0);
      const tax = subtotal * 0.16; // 16% VAT
      return {
        ...prev,
        items: newItems,
        subtotal,
        tax,
        total: subtotal + tax,
      };
    });
  };

  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, { 
        id: Date.now().toString(), 
        description: '', 
        quantity: 1, 
        unitPrice: 0 
      }],
    }));
  };

  const removeItem = (id: string) => {
    if (invoice.items.length === 1) return;
    setInvoice(prev => {
      const newItems = prev.items.filter(item => item.id !== id);
      const subtotal = newItems.reduce((acc, item) => acc + (item.quantity * item.unitPrice), 0);
      const tax = subtotal * 0.16;
      return {
        ...prev,
        items: newItems,
        subtotal,
        tax,
        total: subtotal + tax,
      };
    });
  };

  const handleProcessPayment = () => {
    setInvoice(prev => ({ ...prev, status: 'paid', paymentMethod }));
    setShowPreview(true);
  };

  const handleSendInvoice = () => {
    setInvoice(prev => ({ ...prev, status: 'sent' }));
    alert('Factura enviada por email y WhatsApp!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-5 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm">Nueva Factura</p>
            <p className="text-2xl font-bold text-white">#{invoice.id}</p>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">{invoice.date}</p>
            <p className="text-3xl font-bold text-white">${invoice.total.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Client Info */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)] space-y-4">
        <h4 className="font-semibold text-white flex items-center gap-2">
          <User className="w-5 h-5 text-blue-400" />
          Información del Cliente
        </h4>
        <div>
          <label className="block text-slate-400 text-sm mb-1">Nombre</label>
          <input
            type="text"
            value={invoice.clientName}
            onChange={(e) => setInvoice({...invoice, clientName: e.target.value})}
            placeholder="Nombre del cliente"
            className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-slate-400 text-sm mb-1 flex items-center gap-1">
              <Mail className="w-4 h-4" />
              Email
            </label>
            <input
              type="email"
              value={invoice.clientEmail}
              onChange={(e) => setInvoice({...invoice, clientEmail: e.target.value})}
              placeholder="cliente@email.com"
              className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-slate-400 text-sm mb-1 flex items-center gap-1">
              <Phone className="w-4 h-4" />
              Teléfono
            </label>
            <input
              type="tel"
              value={invoice.clientPhone}
              onChange={(e) => setInvoice({...invoice, clientPhone: e.target.value})}
              placeholder="+58 412 123 4567"
              className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-white">Conceptos</h4>
          <button 
            onClick={addItem}
            className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Agregar
          </button>
        </div>
        
        <div className="space-y-3">
          {invoice.items.map((item, index) => (
            <div key={item.id} className="bg-slate-700/50 p-3 rounded-lg space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                  placeholder="Descripción del servicio"
                  className="flex-1 bg-slate-600 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none text-sm"
                />
                <button 
                  onClick={() => removeItem(item.id)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2">
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => updateItem(item.id, 'quantity', Math.max(1, item.quantity - 1))}
                    className="w-8 h-8 bg-slate-600 rounded flex items-center justify-center text-white"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-16 bg-slate-600 text-white text-center py-2 rounded border border-slate-500"
                  />
                  <button 
                    onClick={() => updateItem(item.id, 'quantity', item.quantity + 1)}
                    className="w-8 h-8 bg-slate-600 rounded flex items-center justify-center text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-2 text-slate-400">$</span>
                  <input
                    type="number"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                    placeholder="Precio"
                    className="w-full bg-slate-600 text-white pl-7 pr-3 py-2 rounded border border-slate-500"
                  />
                </div>
                <div className="w-24 text-right py-2">
                  <span className="text-white font-medium">
                    ${(item.quantity * item.unitPrice).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="border-t border-slate-600 mt-4 pt-4 space-y-2">
          <div className="flex justify-between text-slate-400">
            <span>Subtotal</span>
            <span>${invoice.subtotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span>IVA (16%)</span>
            <span>${invoice.tax.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-slate-600">
            <span>Total</span>
            <span className="text-green-400">${invoice.total.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h4 className="font-semibold text-white mb-3">Método de Pago</h4>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setPaymentMethod('card')}
            className={`p-4 rounded-lg border text-center transition ${
              paymentMethod === 'card' 
                ? 'bg-blue-600 border-blue-500 text-white' 
                : 'bg-slate-700 border-slate-600 text-slate-400'
            }`}
          >
            <CreditCard className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Tarjeta</span>
          </button>
          <button
            onClick={() => setPaymentMethod('cash')}
            className={`p-4 rounded-lg border text-center transition ${
              paymentMethod === 'cash' 
                ? 'bg-green-600 border-green-500 text-white' 
                : 'bg-slate-700 border-slate-600 text-slate-400'
            }`}
          >
            <DollarSign className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Efectivo</span>
          </button>
          <button
            onClick={() => setPaymentMethod('transfer')}
            className={`p-4 rounded-lg border text-center transition ${
              paymentMethod === 'transfer' 
                ? 'bg-purple-600 border-purple-500 text-white' 
                : 'bg-slate-700 border-slate-600 text-slate-400'
            }`}
          >
            <Send className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Transferencia</span>
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button 
          onClick={handleProcessPayment}
          disabled={!invoice.clientName || invoice.items.length === 0}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
        >
          <CheckCircle className="w-6 h-6" />
          Procesar Pago ${invoice.total.toLocaleString()}
        </button>
        
        <div className="flex gap-3">
          <button 
            onClick={handleSendInvoice}
            disabled={!invoice.clientName}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            Enviar Factura
          </button>
          <button className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2">
            <Printer className="w-5 h-5" />
            Imprimir
          </button>
        </div>
      </div>

      {/* Payment Success Modal */}
      {showPreview && invoice.status === 'paid' && (
        <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-green-500/30 w-full max-w-md p-6 text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">¡Pago Exitoso!</h3>
            <p className="text-slate-400 mb-6">
              Factura #{invoice.id} pagada por ${invoice.total.toLocaleString()}
            </p>
            <div className="bg-slate-700/50 p-4 rounded-lg mb-6 text-left">
              <p className="text-slate-400 text-sm">Cliente</p>
              <p className="text-white font-medium">{invoice.clientName}</p>
              <p className="text-slate-400 text-sm mt-2">Método</p>
              <p className="text-white font-medium capitalize">
                {paymentMethod === 'card' ? 'Tarjeta' : paymentMethod === 'cash' ? 'Efectivo' : 'Transferencia'}
              </p>
              <p className="text-slate-400 text-sm mt-2">Fecha</p>
              <p className="text-white font-medium">{new Date().toLocaleString()}</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowPreview(false)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium"
              >
                Nueva Factura
              </button>
              <button 
                onClick={() => setShowPreview(false)}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-medium"
              >
                Ver Historial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
